import React from 'react';
import { useEntityContext } from '../../hooks/useEntityContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { getEntityLabel } from '../../utils/subscriptionUtils';
import {
  ThinkIcon, CommunityIcon, PartnerIcon, ProfileIcon,
  ChevronDown, Lock,
} from '../icons';

/** Map entity type to Phosphor icon component */
function getTypeIcon(type: string) {
  if (type === 'mastermind') return ThinkIcon;
  if (type === 'community') return CommunityIcon;
  if (type === 'networkclub') return PartnerIcon;
  return ProfileIcon;
}

interface EntitySwitcherProps {
  className?: string;
}

/**
 * EntitySwitcher — Dropdown to switch between entities.
 * Uses Phosphor icons instead of emoji.
 */
export function EntitySwitcher({ className = '' }: EntitySwitcherProps) {
  const [user] = useAuthState(auth);
  const { entities, activeEntityId, setActiveEntityId, loading } = useEntityContext(user?.uid);

  if (loading) {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        Laden...
      </div>
    );
  }

  if (entities.length === 0) {
    return null;
  }

  // Single entity — show inline
  if (entities.length === 1) {
    const entity = entities[0];
    const TypeIcon = getTypeIcon(entity.type);
    const isPrivate = !entity.isPublic;

    return (
      <div className={`text-sm text-gray-700 flex items-center gap-2 ${className}`}>
        <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
          <TypeIcon size={14} weight="regular" className="text-gray-500" />
        </div>
        <span className="font-medium">{entity.name}</span>
        {isPrivate && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
            <Lock size={10} strokeWidth={2} />
            Priv\u00e9
          </span>
        )}
      </div>
    );
  }

  // Multiple entities — select dropdown
  const activeEntity = entities.find(e => e.id === activeEntityId);

  return (
    <div className={`relative ${className}`}>
      <select
        value={activeEntityId || ''}
        onChange={(e) => {
          const newEntityId = e.target.value;
          if (newEntityId) {
            setActiveEntityId(newEntityId);
          }
        }}
        className="
          appearance-none bg-white border border-gray-200 rounded-lg
          px-4 py-2 pr-8 text-sm text-gray-700
          focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent
          hover:border-gray-300 transition-colors w-full
        "
      >
        {entities.map(entity => {
          const label = getEntityLabel(entity.type as any);
          const isPrivate = !entity.isPublic;

          return (
            <option key={entity.id} value={entity.id}>
              {entity.name} - {label} {isPrivate ? '(Priv\u00e9)' : ''}
            </option>
          );
        })}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none">
        <ChevronDown size={14} strokeWidth={1.5} className="text-gray-400" />
      </div>
    </div>
  );
}
